/**
 * <ec-read-more> — clamps the product description to N lines (CSS) and shows a
 * "Read more" / "Read less" toggle only when the text is actually longer.
 */
if (!customElements.get('ec-read-more')) {
  class EcReadMore extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('[data-ec-readmore-content]');
      this.toggle = this.querySelector('[data-ec-readmore-toggle]');
      if (!this.content || !this.toggle) return;

      if (!this.bound) {
        this.bound = true;
        this.toggle.addEventListener('click', () => this.setOpen(!this.classList.contains('is-open')));
      }

      this.measure();
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.measure());
        this.resizeObserver.observe(this.content);
      }
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
    }

    measure() {
      if (this.classList.contains('is-open')) return;
      const overflows = this.content.scrollHeight - this.content.clientHeight > 2;
      this.toggle.hidden = !overflows;
      this.classList.toggle('is-clamped', overflows);
    }

    setOpen(open) {
      this.classList.toggle('is-open', open);
      this.toggle.setAttribute('aria-expanded', String(open));
      this.toggle.textContent = open ? this.dataset.lessLabel : this.dataset.moreLabel;
      if (!open) {
        this.measure();
        // keep the collapsed text in view after "Read less" on long descriptions
        const top = this.getBoundingClientRect().top;
        if (top < 0) this.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    }
  }

  customElements.define('ec-read-more', EcReadMore);
}

/**
 * Mobile gallery dots for ec-main-product: one dot per visible image under
 * Dawn's mobile media slider (replaces its arrows + "1 / 2" counter). Dots
 * follow swipes, scroll to their image on tap, and rebuild when Dawn
 * re-orders / filters the media list after a variant change.
 */
(() => {
  const setup = (gallery) => {
    if (gallery.ecDots) return;
    const list = gallery.querySelector('.product__media-list');
    const slider = list?.closest('slider-component');
    if (!list || !slider) return;

    const dots = document.createElement('div');
    dots.className = 'ec-gallery-dots';
    dots.setAttribute('role', 'group');
    dots.setAttribute('aria-label', 'Product images');
    slider.after(dots);
    gallery.ecDots = dots;

    const visibleItems = () =>
      Array.from(list.querySelectorAll(':scope > .product__media-item')).filter(
        (item) => getComputedStyle(item).display !== 'none'
      );

    // item position inside the scrolling list, independent of offsetParent
    const positionOf = (item) => item.getBoundingClientRect().left - list.getBoundingClientRect().left + list.scrollLeft;

    const current = () => {
      const items = visibleItems();
      const left = list.scrollLeft;
      let index = 0;
      items.forEach((item, i) => {
        if (Math.abs(positionOf(item) - left) < Math.abs(positionOf(items[index]) - left)) index = i;
      });
      return index;
    };

    const sync = () => {
      const index = current();
      dots.querySelectorAll('button').forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
    };

    const build = () => {
      const items = visibleItems();
      dots.innerHTML = '';
      dots.hidden = items.length < 2;
      items.forEach((item, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'ec-gallery-dots__dot';
        dot.setAttribute('aria-label', `${i + 1} / ${items.length}`);
        dot.addEventListener('click', () => {
          list.scrollTo({ left: positionOf(item), behavior: 'smooth' });
        });
        dots.appendChild(dot);
      });
      sync();
    };

    // a handful of slides: syncing on every scroll event is cheap
    list.addEventListener('scroll', sync, { passive: true });

    new MutationObserver(build).observe(list, { childList: true, subtree: false, attributes: true, attributeFilter: ['class'] });
    build();
  };

  const init = () => document.querySelectorAll('.ec-product media-gallery').forEach(setup);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
